/** Client project workspace tabs (sidebar sections) */
export const CLIENT_PROJECT_TABS = [
  { id: 'dashboard', label: 'Dashboard', hash: 'insights', icon: '📊' },
  { id: 'cycles', label: 'Test Cycles', hash: 'cycles', icon: '🔄' },
  { id: 'defects', label: 'Defects', hash: 'issues', icon: '🐛' },
  { id: 'forms', label: 'Forms', hash: 'forms', icon: '📋' },
  { id: 'test_cases', label: 'Test cases', hash: 'test-cases', icon: '📝' },
  { id: 'integrations', label: 'Integrations', hash: 'integrations', icon: '🔗' },
];

const HASH_TO_TAB = {
  insights: 'dashboard',
  dashboard: 'dashboard',
  info: 'dashboard',
  cycles: 'cycles',
  issues: 'defects',
  defects: 'defects',
  bugs: 'defects',
  forms: 'forms',
  integrations: 'integrations',
  reports: 'test_cases',
  'test-cases': 'test_cases',
  test_cases: 'test_cases',
  repository: 'forms',
  wiki: 'dashboard',
};

const TAB_ALIASES = {
  insights: 'dashboard',
  issues: 'defects',
  bugs: 'defects',
  reports: 'test_cases',
  integrations: 'integrations',
};

export const STAFF_ROLES = ['admin', 'platform_owner', 'platform_test_manager'];

export function isStaffRole(role) {
  return STAFF_ROLES.includes(role);
}

/** Roles that can create, edit, and review all project test forms (matches API canManageProjectForms). */
export const PROJECT_FORM_MANAGER_ROLES = [
  'admin',
  'platform_owner',
  'platform_test_manager',
  'client',
  'client_team',
];

export function canManageProjectFormsRole(role) {
  return PROJECT_FORM_MANAGER_ROLES.includes(role);
}

/** Nested paths under /app/projects/:projectId (Jira-style shell) */
const PROJECT_TAB_PATHS = {
  dashboard: (projectId) => `/app/projects/${projectId}`,
  cycles: (projectId) => `/app/projects/${projectId}/cycles`,
  defects: (projectId) => `/app/projects/${projectId}/defects`,
  forms: (projectId) => `/app/projects/${projectId}/forms`,
  test_cases: (projectId) => `/app/projects/${projectId}/test-cases`,
  integrations: (projectId) => `/app/projects/${projectId}/integrations`,
  triage: (projectId) => `/app/projects/${projectId}/triage`,
  team: (projectId) => `/app/projects/${projectId}/team`,
  reports: (projectId) => `/app/projects/${projectId}/reports`,
  wiki: (projectId) => `/app/projects/${projectId}/wiki`,
};

/** Map ProjectDetail tab hashes to nested shell paths */
const HASH_TO_NESTED_TAB = {
  insights: 'dashboard',
  info: 'dashboard',
  dashboard: 'dashboard',
  wiki: 'wiki',
  repository: 'test_cases',
  'test-cases': 'test_cases',
  test_cases: 'test_cases',
  cycles: 'cycles',
  issues: 'defects',
  defects: 'defects',
  bugs: 'defects',
  triage: 'triage',
  team: 'team',
  reports: 'reports',
  integrations: 'integrations',
  forms: 'forms',
};

/** @deprecated Legacy global routes — use redirects in App.js */
export const PROJECT_EXTERNAL_TAB_PATHS = PROJECT_TAB_PATHS;

export function normalizeProjectTabId(raw) {
  if (!raw) return 'dashboard';
  const key = String(raw).trim().toLowerCase();
  return TAB_ALIASES[key] || key;
}

export function resolveProjectTab(searchParams, hash = '') {
  const fromQuery = searchParams?.get?.('tab');
  if (fromQuery) return normalizeProjectTabId(fromQuery);
  const h = (hash || '').replace(/^#/, '').trim();
  if (!h) return 'dashboard';
  return normalizeProjectTabId(HASH_TO_TAB[h] || h);
}

export function buildProjectUrl(projectId, tabId = 'dashboard') {
  if (!projectId) return '/app/projects';
  const tab = normalizeProjectTabId(tabId);
  const fn = PROJECT_TAB_PATHS[tab] || PROJECT_TAB_PATHS.dashboard;
  return fn(projectId);
}

export function buildProjectUrlFromHash(projectId, hash = '') {
  const h = String(hash || '').replace(/^#/, '').trim();
  if (!h) return buildProjectUrl(projectId, 'dashboard');
  const tab = HASH_TO_NESTED_TAB[h] || normalizeProjectTabId(h);
  return buildProjectUrl(projectId, tab);
}

export function resolveNestedTabFromPath(pathname = '') {
  const m = String(pathname).match(
    /^\/app\/projects\/[^/]+\/(cycles|defects|test-cases|forms|integrations|triage|team|reports|wiki)(?:\/|$)/
  );
  if (!m) return '';
  if (m[1] === 'test-cases') return 'test_cases';
  return m[1];
}

export function buildProjectNavItems(projectId, role) {
  if (!projectId) return [];
  const items = [
    { label: 'Dashboard', icon: '📊', path: buildProjectUrl(projectId, 'dashboard') },
    {
      label: 'Test Cycles',
      icon: '🔄',
      path: buildProjectUrl(projectId, 'cycles'),
      badgeKey: 'cycles',
      badgeColor: 'blue',
    },
    { label: 'Defects', icon: '🐛', path: buildProjectUrl(projectId, 'defects'), badgeKey: 'bugs' },
    { label: 'Test cases', icon: '📝', path: buildProjectUrl(projectId, 'test_cases') },
    { label: 'Tester Coverage', icon: '🌐', path: '/app/tester-coverage' },
    { label: 'Forms', icon: '📋', path: buildProjectUrl(projectId, 'forms') },
    { label: 'Integrations', icon: '🔗', path: buildProjectUrl(projectId, 'integrations') },
  ];
  if (isStaffRole(role)) {
    items.push(
      { label: 'Issues Triage', icon: '⚖️', path: buildProjectUrl(projectId, 'triage') },
      { label: 'Project Team', icon: '👥', path: buildProjectUrl(projectId, 'team') },
      { label: 'Reports', icon: '📈', path: buildProjectUrl(projectId, 'reports') }
    );
  }
  return items;
}

/** Org-level sidebar when not inside a project (staff) */
export function buildOrgNavItems(role) {
  const items = [
    { label: 'Dashboard', icon: '📊', path: '/app/dashboard' },
    { label: 'Chat', icon: '💬', path: '/app/messages', badgeKey: 'unreadMessages' },
    { label: 'Projects', icon: '📁', path: '/app/projects' },
  ];
  if (role === 'platform_owner' || role === 'admin') {
    items.push(
      { label: 'Registered Testers', icon: '👤', path: '/app/users' },
      { label: 'Import Testers', icon: '📤', path: '/app/admin/import-testers-devices' },
      { label: 'Lead Management', icon: '📇', path: '/app/leads' },
      { label: 'Clients', icon: '🏢', path: '/app/clients' },
      { label: 'Device Coverage', icon: '📱', path: '/app/coverage' },
      { label: 'Tester Coverage', icon: '🌐', path: '/app/tester-coverage' },
      { label: 'Identity review', icon: '✅', path: '/app/kyc-review' },
      // Staff use ProjectShellLayout, so this must live here (DashboardLayout alone never shows for owners).
      { label: 'Payout approvals', icon: '💰', path: '/app/payout-approvals' },
      { label: 'Deletion requests', icon: '🗑️', path: '/app/account-deletion-requests' },
      { label: 'Billing', icon: '💳', path: '/app/billing' },
      { label: 'Reports', icon: '📈', path: '/app/reports' },
      // Owners/admins only, matching the route guard. Added here rather than in
      // DashboardLayout: staff render ProjectShellLayout, whose sidebar is built
      // from this list, so a nav entry added to DashboardLayout never appears.
      { label: 'Acquisition', icon: '📊', path: '/app/reports/acquisition' },
      { label: 'Command Center', icon: '🤖', path: '/app/command-center' },
      // The Platform Admin hub — campaign analytics, App-Testify sync, revenue,
      // audit logs, BTS integrations — had no entry point in this sidebar at
      // all, so every screen behind it was reachable only by typing the URL.
      { label: 'Platform Admin', icon: '🛠️', path: '/app/admin' }
    );
  } else if (isStaffRole(role)) {
    items.push(
      { label: 'Test Cycles', icon: '🔄', path: '/app/test-cycles' },
      { label: 'Identity review', icon: '✅', path: '/app/kyc-review' },
      { label: 'Payout approvals', icon: '💰', path: '/app/payout-approvals' },
      { label: 'Platform Admin', icon: '🛠️', path: '/app/admin' }
    );
  }
  return items;
}

/** Routes staff may visit without a project selected */
export const STAFF_PATHS_WITHOUT_PROJECT = [
  '/app/dashboard',
  '/app/messages',
  '/app/projects',
  '/app/users',
  '/app/admin',
  '/app/admin/import-testers-devices',
  '/app/command-center',
  '/app/finance-reports',
  '/app/leads',
  '/app/clients',
  '/app/coverage',
  '/app/tester-coverage',
  '/app/kyc-review',
  '/app/payout-approvals',
  '/app/account-deletion-requests',
  '/app/billing',
  '/app/reports',
  '/app/test-cycles',
  '/app/bug-triage',
  '/app/settings',
  '/app/bugs',
];

export function isStaffPathAllowedWithoutProject(pathname) {
  if (STAFF_PATHS_WITHOUT_PROJECT.includes(pathname)) return true;
  // Every platform admin screen is org-wide, never scoped to a project. Matched
  // by prefix rather than listed one by one: only /app/admin/import-testers-devices
  // was ever listed, so the hub itself plus audit logs, BTS integrations, revenue
  // and campaign analytics all silently bounced staff to /app/projects, and each
  // new admin page would have been stranded the same way.
  if (pathname === '/app/admin' || pathname.startsWith('/app/admin/')) return true;
  // Reports are org-wide too. `/app/reports` was allowed but only as an exact
  // match, so /app/reports/acquisition bounced staff to /app/projects the
  // moment it shipped. Prefix-match so report sub-pages don't repeat that.
  if (pathname.startsWith('/app/reports/')) return true;
  if (pathname.startsWith('/app/bugs/')) return true;
  if (pathname.startsWith('/app/clients/')) return true;
  if (pathname.startsWith('/app/testers/')) return true;
  if (pathname.startsWith('/app/users/')) return true;
  return false;
}

/** Extract project id from nested client routes */
export function parseProjectIdFromPath(pathname = '') {
  const m = String(pathname).match(/^\/app\/projects\/([^/]+)/);
  if (!m) return '';
  const segment = m[1];
  if (segment === 'new') return '';
  return segment;
}

export function clientProjectCyclesPath(projectId) {
  return projectId ? `/app/projects/${projectId}/cycles` : '/app/projects';
}

export function clientProjectCyclesNewPath(projectId) {
  return `/app/projects/${projectId}/cycles/new`;
}

export function clientProjectCycleWorkspacePath(projectId, cycleId, tab = 'setup') {
  if (projectId && cycleId) {
    return `/app/projects/${projectId}/cycles/${cycleId}/workspace/${tab}`;
  }
  if (cycleId) return `/app/test-cycles/${cycleId}/workspace/${tab}`;
  return '/app/projects';
}

export function cycleTestCaseExecutePath(projectId, cycleId, testCaseId, { readOnly = false, testerId = null } = {}) {
  const base = projectId
    ? `/app/projects/${projectId}/cycles/${cycleId}/workspace/test-cases/${testCaseId}/execute`
    : `/app/test-cycles/${cycleId}/workspace/test-cases/${testCaseId}/execute`;
  const params = new URLSearchParams();
  if (readOnly) params.set('mode', 'view');
  if (testerId) params.set('testerId', testerId);
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

export function cycleCreateIssuePath(projectId, cycleId) {
  if (projectId && cycleId) {
    return `/app/projects/${projectId}/cycles/${cycleId}/create-issue`;
  }
  if (cycleId) return `/app/test-cycles/${cycleId}/create-issue`;
  return '/app/projects';
}

export function clientDefectsPath(projectId = '') {
  return projectId ? `/app/projects/${projectId}/defects` : '/app/projects';
}

/** Defect detail page (API remains /bugs). */
export function defectDetailPath(defectId) {
  return defectId ? `/app/bugs/${defectId}` : '/app/projects';
}

export function clientCyclesListPath(projectId = '') {
  return clientProjectCyclesPath(projectId);
}

export function buildTestCasesUrl({ projectId = '' } = {}) {
  return projectId ? `/app/projects/${projectId}/test-cases` : '/app/projects';
}

export const REPORT_TIME_RANGES = [
  { value: '7days', label: 'Last 7 days' },
  { value: '30days', label: 'Last 30 days' },
  { value: '90days', label: 'Last 90 days' },
  { value: '1year', label: 'Last year' },
];

export function normalizeReportRange(raw) {
  const v = String(raw || '30days')
    .trim()
    .toLowerCase();
  if (REPORT_TIME_RANGES.some((r) => r.value === v)) return v;
  return '30days';
}

export function buildReportsUrl({ projectId = '' } = {}) {
  const sp = new URLSearchParams();
  if (projectId) sp.set('projectId', projectId);
  const q = sp.toString();
  return q ? `/app/reports?${q}` : '/app/reports';
}

export function buildIntegrationsUrl({ projectId = '' } = {}) {
  return projectId ? `/app/projects/${projectId}/integrations` : '/app/projects';
}

export function buildScopedAppPath(basePath, projectId = '') {
  if (basePath === '/app/test-cycles' && projectId) return clientProjectCyclesPath(projectId);
  if (basePath === '/app/defects' && projectId) return clientDefectsPath(projectId);
  if (basePath === '/app/test-cases' && projectId) return buildTestCasesUrl({ projectId });
  if (basePath === '/app/integrations' && projectId) return buildIntegrationsUrl({ projectId });
  if (basePath === '/app/forms' && projectId) return buildProjectUrl(projectId, 'forms');
  if (!projectId) return basePath;
  const sp = new URLSearchParams();
  sp.set('projectId', projectId);
  return `${basePath}?${sp.toString()}`;
}

/** Routes clients may visit without a project selected */
export const CLIENT_PATHS_WITHOUT_PROJECT = [
  '/app/projects',
  '/app/tester-coverage',
  '/app/team',
  '/app/billing',
  '/app/settings',
];

export function isClientPathAllowedWithoutProject(pathname) {
  if (CLIENT_PATHS_WITHOUT_PROJECT.includes(pathname)) return true;
  if (pathname.startsWith('/app/bugs/')) return true;
  return false;
}
